import { useMemo, useState } from 'react';
import { Alert, Button, Modal, Table, message } from 'antd';
import { BarChartOutlined } from '@ant-design/icons';
import type { PartnerRule } from '@shipium-corp/shipium-ts-types';
import type { RuleImpactFilterResult } from '~/utils/ruleImpactCount/ruleCriteriaToTypesenseFilter';
import {
  type RuleImpactCountResult,
  type ExampleShipment,
  useRuleImpactCount,
} from '~/hooks/queries/useRuleImpactCount';
import { useCarriers, useServiceMethods } from '~/hooks/queries/useCarriersFiltered';
import { useSession } from '~/hooks/useSession';
import { format } from 'date-fns';

function ExampleShipmentsTable({
  shipments,
  sessionPartnerUrl,
  resolveName,
}: {
  shipments: ExampleShipment[];
  sessionPartnerUrl: string;
  resolveName: (id: string) => string;
}) {
  if (!shipments.length) return null;

  return (
    <Table
      size="small"
      dataSource={shipments}
      rowKey="shipiumShipmentId"
      pagination={false}
      style={{ marginTop: 12 }}
      onRow={(record) => ({
        style: { cursor: 'pointer' },
        onClick: () => {
          window.open(`/${sessionPartnerUrl}/delivery-reporting/shipment/${record.shipiumShipmentId}`, '_blank');
        },
      })}
      columns={[
        {
          title: 'Carrier',
          dataIndex: 'carrierId',
          ellipsis: true,
          render: (id: string) => resolveName(id),
        },
        {
          title: 'Service Method',
          dataIndex: 'serviceMethodId',
          ellipsis: true,
          render: (id: string) => resolveName(id),
        },
        {
          title: 'Destination',
          dataIndex: 'destination',
          ellipsis: true,
        },
        {
          title: 'Created',
          dataIndex: 'createdDateTime',
          render: (ts: number) => format(new Date(ts), 'MM/dd/yyyy'),
        },
      ]}
    />
  );
}

type RuleListType = PartnerRule['listType'];

function carrierMethodPhrasing(listType: RuleListType): { verb: string; suffix: string } {
  if (listType === 'block') {
    return { verb: 'are currently routed', suffix: ' and would be re-routed' };
  }
  return { verb: 'are already routed', suffix: '' };
}

function ImpactResultModal({
  result,
  carrierMethodList,
  listType,
  onClose,
}: {
  result: RuleImpactCountResult;
  carrierMethodList?: string[];
  listType: RuleListType;
  onClose: () => void;
}) {
  const { sessionPartnerUrl } = useSession();
  const carriersQuery = useCarriers();
  const serviceMethodsQuery = useServiceMethods();

  const resolveName = useMemo(() => {
    const carrierMap = new Map((carriersQuery.data ?? []).map((c) => [c.carrierId, c.name]));
    const methodMap = new Map((serviceMethodsQuery.data ?? []).map((m) => [m.serviceMethodId, m.name]));
    return (id: string): string => carrierMap.get(id) ?? methodMap.get(id) ?? id;
  }, [carriersQuery.data, serviceMethodsQuery.data]);

  const formattedCount = result.count.toLocaleString();
  const formattedTotal = result.total.toLocaleString();
  const percentage = result.total > 0 ? ((result.count / result.total) * 100).toFixed(1) : '0';
  const resolvedNames = carrierMethodList?.map(resolveName);
  const { verb, suffix } = carrierMethodPhrasing(listType);

  return (
    <Modal title="Rule Impact" width={800} open onCancel={onClose} footer={null}>
      {result.hasUnsupportedCriteria && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Approximate count"
          description="Some of this rule's criteria couldn't be applied to the shipment search. The number below is an upper bound — actual impact may be smaller."
        />
      )}
      <p>
        <strong>{formattedCount}</strong> of <strong>{formattedTotal}</strong> shipments ({percentage}%) in the last 7
        days match this rule&apos;s criteria.
      </p>
      {result.carrierMethodCount != null && result.count > 0 && (
        <p>
          Of those, <strong>{result.carrierMethodCount.toLocaleString()}</strong> (
          {((result.carrierMethodCount / result.count) * 100).toFixed(1)}%) {verb} via{' '}
          {resolvedNames?.length ? (
            <strong>{resolvedNames.join(', ')}</strong>
          ) : (
            "the rule's listed carrier(s)/method(s)"
          )}
          {suffix}.
        </p>
      )}
      <ExampleShipmentsTable
        shipments={result.exampleShipments}
        sessionPartnerUrl={sessionPartnerUrl}
        resolveName={resolveName}
      />
    </Modal>
  );
}

interface PendingResult {
  result: RuleImpactCountResult;
  carrierMethodList?: string[];
}

export function RuleImpactCountButton({
  getFilterResult,
  listType,
  disabled = false,
}: {
  getFilterResult: () => RuleImpactFilterResult;
  listType: RuleListType;
  disabled?: boolean;
}) {
  const ruleImpactCount = useRuleImpactCount();
  const [pending, setPending] = useState<PendingResult | null>(null);

  return (
    <>
      <Button
        icon={<BarChartOutlined />}
        loading={ruleImpactCount.isPending}
        disabled={disabled || ruleImpactCount.isPending}
        onClick={() => {
          const filterResult = getFilterResult();
          ruleImpactCount.mutate(filterResult, {
            onSuccess: (result) => setPending({ result, carrierMethodList: filterResult.carrierAndServiceMethodList }),
            onError: (error) => {
              console.error('Rule impact count error:', error);
              message.error('Failed to check rule impact. Please try again.');
            },
          });
        }}
      >
        Check Impact
      </Button>
      {pending && (
        <ImpactResultModal
          result={pending.result}
          carrierMethodList={pending.carrierMethodList}
          listType={listType}
          onClose={() => setPending(null)}
        />
      )}
    </>
  );
}
